import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
} from "@mui/material";
import {
  AccountBalanceOutlined,
  CategoryOutlined,
  ChatBubbleOutline,
  PersonOutline,
  SubdirectoryArrowRightRounded,
  SupportAgentOutlined,
  ThumbDownAltOutlined,
  ThumbUpAltOutlined,
  VideoLibraryOutlined,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  AdminPageLayout,
  AdminSectionCard,
  AdminAction,
} from "../../components/admin";
import { resolveMediaUrl } from "../../utils/mediaUrl";
import { getDashboardStats } from "./index";

const SUMMARY_CARDS = [
  {
    key: "totalVideos",
    title: "Total Videos",
    hint: "Video library",
    path: "/videoUpload",
    icon: VideoLibraryOutlined,
  },
  {
    key: "totalCategories",
    title: "Categories",
    hint: "Content structure",
    path: "/category",
    icon: CategoryOutlined,
  },
  {
    key: "totalSubcategories",
    title: "Sub Categories",
    hint: "Mapped sub categories",
    path: "/subCategory",
    icon: SubdirectoryArrowRightRounded,
  },
  {
    key: "totalGovernmentSchemesAndTraining",
    title: "Gov. Schemes & Training",
    hintFormatter: (summary) =>
      `${formatCount(summary?.totalSchemes)} schemes · ${formatCount(
        summary?.totalTrainingPrograms
      )} training`,
    path: "/government-schemes",
    icon: AccountBalanceOutlined,
  },
  {
    key: "totalSupportTickets",
    title: "Support Tickets",
    hint: "Open support inbox",
    path: "/support",
    icon: SupportAgentOutlined,
  },
];

function formatCount(value) {
  return Number(value || 0).toLocaleString("en-IN");
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function truncate(text, max = 120) {
  const value = String(text || "").trim();
  if (!value) return "—";
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}

function statusChipColor(status) {
  if (status === "resolved" || status === "closed") return "success";
  if (status === "in_progress") return "warning";
  return "info";
}

function normalizeDashboardPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  return {
    summary: payload.summary && typeof payload.summary === "object" ? payload.summary : {},
    latestComments: Array.isArray(payload.latestComments) ? payload.latestComments : [],
    recentUsers: Array.isArray(payload.recentUsers) ? payload.recentUsers : [],
    mostLikedVideos: Array.isArray(payload.mostLikedVideos) ? payload.mostLikedVideos : [],
    mostDislikedVideos: Array.isArray(payload.mostDislikedVideos)
      ? payload.mostDislikedVideos
      : [],
    latestSupportTickets: Array.isArray(payload.latestSupportTickets)
      ? payload.latestSupportTickets
      : [],
  };
}

function WidgetHeader({ title, viewAllTo, viewAllLabel = "View all" }) {
  return (
    <div className="admin-widget__toolbar">
      <h3 className="admin-widget__heading">{title}</h3>
      {viewAllTo ? (
        <Link to={viewAllTo} className="admin-widget__view-all">
          {viewAllLabel}
        </Link>
      ) : null}
    </div>
  );
}

function EmptyWidget({ message }) {
  return <div className="admin-widget__empty">{message}</div>;
}

function Thumbnail({ src, alt = "" }) {
  const [failed, setFailed] = useState(false);
  const url = resolveMediaUrl(src);

  if (!url || failed) {
    return (
      <div className="admin-widget-thumb">
        <VideoLibraryOutlined fontSize="small" />
      </div>
    );
  }

  return (
    <div className="admin-widget-thumb">
      <img src={url} alt={alt} loading="lazy" onError={() => setFailed(true)} />
    </div>
  );
}

function CommentsWidget({ comments }) {
  if (!comments?.length) {
    return <EmptyWidget message="No video comments yet." />;
  }

  return (
    <ul className="admin-widget-list">
      {comments.map((comment) => {
        const videoId = comment.video?.id;
        const videoTitle = comment.video?.title || "Untitled video";
        const userName = comment.user?.name || "Unknown user";
        const avatarUrl = resolveMediaUrl(comment.user?.imageUrl);
        const commentState = {
          videoTitle,
          from: "/dashboard",
        };

        return (
          <li key={comment.id} className="admin-widget-list__item">
            <Avatar
              src={avatarUrl || undefined}
              alt={userName}
              sx={{
                width: 36,
                height: 36,
                bgcolor: "rgba(63,122,42,0.15)",
                color: "#3f7a2a",
                fontSize: 14,
              }}
            >
              {userName.charAt(0).toUpperCase()}
            </Avatar>
            <div className="admin-widget-list__body">
              <div className="admin-widget-list__meta">
                <strong>{userName}</strong>
                <span>{formatDateTime(comment.createdAt)}</span>
              </div>
              <p className="admin-widget-list__text">
                {comment.parentId ? (
                  <Chip
                    size="small"
                    label="Reply"
                    sx={{ height: 20, mr: 0.75, fontSize: 11 }}
                  />
                ) : null}
                {truncate(comment.content, 140)}
              </p>
              <div className="admin-widget-list__footer">
                <span className="admin-widget-list__subtle">
                  <ChatBubbleOutline sx={{ fontSize: 14 }} />
                  {truncate(videoTitle, 48)}
                </span>
                {videoId ? (
                  <Link
                    to={`/comments/${videoId}`}
                    state={commentState}
                    className="admin-widget__inline-link"
                  >
                    Manage comments
                  </Link>
                ) : null}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function UsersWidget({ users }) {
  if (!users?.length) {
    return <EmptyWidget message="No recently registered users." />;
  }

  return (
    <ul className="admin-widget-list">
      {users.map((user) => {
        const avatarUrl = resolveMediaUrl(user.imageUrl);
        const contact = user.email || user.mobile || "No contact";
        const location = [user.city, user.state].filter(Boolean).join(", ");

        return (
          <li key={user.id} className="admin-widget-list__item">
            <Avatar
              src={avatarUrl || undefined}
              alt={user.name}
              sx={{
                width: 36,
                height: 36,
                bgcolor: "rgba(63,122,42,0.15)",
                color: "#3f7a2a",
                fontSize: 14,
              }}
            >
              {(user.name || "?").charAt(0).toUpperCase()}
            </Avatar>
            <div className="admin-widget-list__body">
              <div className="admin-widget-list__meta">
                <strong>{user.name}</strong>
                <span>{formatDateTime(user.createdAt)}</span>
              </div>
              <p className="admin-widget-list__text">{contact}</p>
              <div className="admin-widget-list__footer">
                {location ? (
                  <span className="admin-widget-list__subtle">{location}</span>
                ) : (
                  <span />
                )}
                <Link to="/users" className="admin-widget__inline-link">
                  View users
                </Link>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function EngagementVideosList({ videos, mode }) {
  if (!videos?.length) {
    return (
      <EmptyWidget
        message={
          mode === "liked" ? "No liked videos yet." : "No disliked videos yet."
        }
      />
    );
  }

  return (
    <ul className="admin-widget-list admin-widget-list--videos">
      {videos.map((video) => (
        <li
          key={`${mode}-${video.id}`}
          className="admin-widget-list__item admin-widget-list__item--video"
        >
          <Thumbnail src={video.thumbnailUrl} alt={video.title || ""} />
          <div className="admin-widget-list__body">
            <div className="admin-widget-list__meta">
              <strong title={video.title}>{truncate(video.title, 64)}</strong>
            </div>
            <div className="admin-widget-list__footer admin-widget-list__footer--stats">
              <span className="admin-widget-stat admin-widget-stat--like">
                <ThumbUpAltOutlined sx={{ fontSize: 14 }} />
                {formatCount(video.likes)}
              </span>
              <span className="admin-widget-stat admin-widget-stat--dislike">
                <ThumbDownAltOutlined sx={{ fontSize: 14 }} />
                {formatCount(video.dislikes)}
              </span>
              {video.id ? (
                <>
                  <Link
                    to={`/videoUpload/${video.id}`}
                    className="admin-widget__inline-link"
                  >
                    Open video
                  </Link>
                  <Link
                    to={`/comments/${video.id}`}
                    state={{
                      videoTitle: video.title,
                      videoLanguage: video.language,
                      videoCategory: video.category,
                      from: "/dashboard",
                    }}
                    className="admin-widget__inline-link"
                  >
                    Comments
                  </Link>
                </>
              ) : null}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function EngagementWidget({ mostLikedVideos, mostDislikedVideos }) {
  const [tab, setTab] = useState(0);

  return (
    <div>
      <Tabs
        value={tab}
        onChange={(_, next) => setTab(next)}
        sx={{
          minHeight: 40,
          mb: 1.5,
          "& .MuiTabs-indicator": {
            backgroundColor: "#3f7a2a",
            height: 3,
            borderRadius: "3px 3px 0 0",
          },
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 500,
            color: "#62715d",
            minHeight: 40,
            "&.Mui-selected": { color: "#3f7a2a", fontWeight: 700 },
          },
        }}
      >
        <Tab
          icon={<ThumbUpAltOutlined fontSize="small" />}
          iconPosition="start"
          label="Most Liked"
        />
        <Tab
          icon={<ThumbDownAltOutlined fontSize="small" />}
          iconPosition="start"
          label="Most Disliked"
        />
      </Tabs>
      {tab === 0 ? (
        <EngagementVideosList videos={mostLikedVideos} mode="liked" />
      ) : (
        <EngagementVideosList videos={mostDislikedVideos} mode="disliked" />
      )}
    </div>
  );
}

function SupportTicketsWidget({ tickets }) {
  if (!tickets?.length) {
    return <EmptyWidget message="No support tickets yet." />;
  }

  return (
    <ul className="admin-widget-list">
      {tickets.map((ticket) => {
        const userName = ticket.user?.name || "Unknown user";
        return (
          <li key={ticket.id} className="admin-widget-list__item">
            <div className="admin-widget-ticket-id">#{ticket.id}</div>
            <div className="admin-widget-list__body">
              <div className="admin-widget-list__meta">
                <strong>{truncate(ticket.title, 56)}</strong>
                <span>{formatDateTime(ticket.createdAt)}</span>
              </div>
              <div className="admin-widget-list__footer">
                <span className="admin-widget-list__subtle">
                  <PersonOutline sx={{ fontSize: 14 }} />
                  {userName}
                </span>
                <div className="admin-widget-list__actions">
                  <Chip
                    size="small"
                    label={String(ticket.status || "open").replace("_", " ")}
                    color={statusChipColor(ticket.status)}
                    sx={{ height: 22, textTransform: "capitalize" }}
                  />
                  <Link
                    to={`/support?ticketId=${ticket.id}`}
                    className="admin-widget__inline-link"
                  >
                    Open ticket
                  </Link>
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const summary = data?.summary || {};

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getDashboardStats();
      if (response.data?.status && response.data.status !== "ok") {
        throw new Error(response.data?.message || "Invalid dashboard response");
      }
      const normalized = normalizeDashboardPayload(response.data?.data);
      if (!normalized) {
        throw new Error("Invalid dashboard response");
      }
      setData(normalized);
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Failed to load dashboard";
      setError(message);
      setData(null);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  return (
    <AdminPageLayout
      eyebrow="Overview"
      title="Dashboard"
      description="Recent activity, video engagement, and support inbox highlights across the admin panel."
      actions={
        <div className="admin-toolbar">
          <AdminAction variant="outline" onClick={fetchDashboard} disabled={loading}>
            Refresh
          </AdminAction>
        </div>
      }
    >
      {loading ? (
        <div className="admin-surface">
          <Box
            className="admin-loading-state"
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1.5}
          >
            <CircularProgress size={28} sx={{ color: "#3f7a2a" }} />
            <span>Loading dashboard…</span>
          </Box>
        </div>
      ) : null}

      {!loading && error ? (
        <div className="admin-surface">
          <div className="admin-empty-state">
            <p>{error}</p>
            <div style={{ marginTop: 16 }}>
              <AdminAction onClick={fetchDashboard}>Try again</AdminAction>
            </div>
          </div>
        </div>
      ) : null}

      {!loading && !error && data ? (
        <>
          <div className="admin-overview-grid admin-overview-grid--compact">
            {SUMMARY_CARDS.map((card) => {
              const Icon = card.icon;
              const value = summary?.[card.key];
              const hint = card.hintFormatter
                ? card.hintFormatter(summary)
                : card.hint;

              return (
                <Link
                  key={card.key}
                  to={card.path}
                  className="admin-overview-card"
                  aria-label={`Open ${card.title}`}
                >
                  <div className="admin-overview-card__icon">
                    <Icon fontSize="small" />
                  </div>
                  <div className="admin-overview-card__body">
                    <span className="admin-overview-card__title">{card.title}</span>
                    <strong className="admin-overview-card__value">
                      {formatCount(value)}
                    </strong>
                    {hint ? (
                      <span className="admin-overview-card__hint">{hint}</span>
                    ) : null}
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="admin-widget-grid">
            <AdminSectionCard className="admin-widget">
              <WidgetHeader
                title="Latest Video Comments"
                viewAllTo="/dashboards/engagement"
                viewAllLabel="Engagement"
              />
              <CommentsWidget comments={data.latestComments} />
            </AdminSectionCard>

            <AdminSectionCard className="admin-widget">
              <WidgetHeader title="Newly Registered Users" viewAllTo="/users" />
              <UsersWidget users={data.recentUsers} />
            </AdminSectionCard>

            <AdminSectionCard className="admin-widget">
              <WidgetHeader
                title="Video Engagement"
                viewAllTo="/dashboards/engagement"
              />
              <EngagementWidget
                mostLikedVideos={data.mostLikedVideos}
                mostDislikedVideos={data.mostDislikedVideos}
              />
            </AdminSectionCard>

            <AdminSectionCard className="admin-widget">
              <WidgetHeader title="Latest Support Tickets" viewAllTo="/support" />
              <SupportTicketsWidget tickets={data.latestSupportTickets} />
            </AdminSectionCard>
          </div>
        </>
      ) : null}

      {!loading && !error && !data ? (
        <div className="admin-surface">
          <div className="admin-empty-state">No dashboard data available.</div>
        </div>
      ) : null}
    </AdminPageLayout>
  );
}
